import { useState, useEffect, useRef } from 'react';
import { useScrollReveal } from '../../../hooks/useScrollReveal';

interface CounterProps {
  target: string;
  isVisible: boolean;
  delay?: number;
}

function AnimatedCounter({ target, isVisible, delay = 0 }: CounterProps) {
  const [display, setDisplay] = useState('0');
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;

    const numericMatch = target.match(/[\d.]+/);
    if (!numericMatch) {
      setDisplay(target);
      return;
    }

    const numericValue = parseFloat(numericMatch[0]);
    const suffix = target.replace(/[\d.]+/, '');
    const isDecimal = numericMatch[0].includes('.');
    const duration = 1800;
    const steps = 60;
    const stepTime = duration / steps;

    let step = 0;

    const timer = setTimeout(() => {
      hasAnimated.current = true;
      const interval = setInterval(() => {
        step++;
        const progress = step / steps;
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = eased * numericValue;
        setDisplay(`${isDecimal ? current.toFixed(1) : Math.floor(current)}${suffix}`);
        if (step >= steps) {
          setDisplay(target);
          clearInterval(interval);
        }
      }, stepTime);
    }, delay);

    return () => clearTimeout(timer);
  }, [isVisible, target, delay]);

  return <>{display}</>;
}

export default function Differential() {
  const { ref: quoteRef, isVisible: quoteVisible } = useScrollReveal(0.1);
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal(0.1);

  const stats = [
    { value: '98%', label: 'Satisfação' },
    { value: '150+', label: 'Projetos' },
    { value: '5x', label: 'Mais Rápido' },
    { value: '24/7', label: 'Suporte' },
  ];

  return (
    <section id="differential" className="py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div className="text-center">

          <div
            ref={quoteRef}
            className={`transition-all ease-out ${
              quoteVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
            style={{ transitionDuration: '800ms' }}
          >
            <h2 className="text-4xl md:text-5xl font-extralight text-gray-900 mb-6">
              Por que uma <span className="font-light text-purple-600">Alma?</span>
            </h2>
            <div className="w-20 h-0.5 bg-purple-600 mx-auto mb-12"></div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-100/50 via-transparent to-purple-100/50 rounded-3xl"></div>
              <div className="relative bg-white/80 backdrop-blur-sm p-12 md:p-16 rounded-3xl border border-purple-100 shadow-xl">
                <p className="text-xl md:text-2xl text-gray-700 font-light leading-relaxed mb-8">
                  No digital, <strong className="font-normal text-purple-600">tempo é respeito</strong>.
                  Nossa metodologia foi projetada para entregar excelência com um ritmo superior,
                  garantindo que sua marca não perca o fôlego do mercado.
                </p>
                <p className="text-lg md:text-xl text-gray-600 font-light leading-relaxed">
                  Unimos o afeto de uma parceria verdadeira com a precisão de uma liderança firme.
                </p>
              </div>
            </div>
          </div>

          <div
            ref={statsRef}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
          >
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center transition-all ease-out ${
                  statsVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-90'
                }`}
                style={{
                  transitionDuration: '600ms',
                  transitionDelay: statsVisible ? `${index * 150}ms` : '0ms',
                  transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                }}
              >
                <div className="text-4xl md:text-5xl font-light text-purple-600 mb-2">
                  <AnimatedCounter target={stat.value} isVisible={statsVisible} delay={index * 150} />
                </div>
                <p className="text-sm text-gray-600 font-light">{stat.label}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
}
